import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  PolicePresenceReason,
  PolicePresenceRequested,
} from "@thalamos/common";

import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  TextField,
} from "~/components/design-system/index.js";

export type RequestPolicePresenceDialogProps = {
  onClose: () => void;
  onRequestPresence: (data: {
    reasonValue: PolicePresenceReason;
    reasonLabel: string;
    furtherReasoning: string;
  }) => void;
  onDontRequestPresence: () => void;
  isDisabled: boolean;
  officerName: string;
  version: number;
  onGoBack?: () => void;
  policePresenceRequestData?: PolicePresenceRequested["policePresenceRequestData"];
};

export function RequestPolicePresentDialog({
  onClose,
  onDontRequestPresence,
  onRequestPresence,
  isDisabled,
  officerName,
  version,
  onGoBack,
  policePresenceRequestData,
}: RequestPolicePresenceDialogProps) {
  const { t } = useTranslation();

  const reasonOptions = [
    {
      value: "immediateRiskToLifeOrOfSeriousHarm",
      label: t(
        "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.options.immediateRiskToLifeOrOfSeriousHarm",
      ),
    },
    {
      value: "notEnoughStaff",
      label: t(
        "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.options.notEnoughStaff",
      ),
    },
    {
      value: "transfer",
      label: t(
        "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.options.transfer",
      ),
    },
    {
      value: "other",
      label: t(
        "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.options.other",
      ),
    },
  ];

  const [requestPresenceResponse, setRequestPresenceResponse] = useState<
    "yes" | "no" | null
  >(null);

  // extra info to be captured on the 'yes' branch
  const [selectedReason, setSelectedReason] = useState<{
    value: PolicePresenceReason;
    label: string;
  } | null>(null);
  const [furtherReasoning, setFurtherReasoning] = useState<string>("");

  // Populate state if we have existing data (i.e. user is going back to this dialog)
  useEffect(() => {
    if (policePresenceRequestData) {
      setRequestPresenceResponse(
        policePresenceRequestData.hasPolicePresenceRequested.value,
      );
      setSelectedReason({
        value: policePresenceRequestData.policePresenceRequestedReason.value,
        label:
          policePresenceRequestData.policePresenceRequestedReason.valueLabel,
      });
      setFurtherReasoning(policePresenceRequestData.furtherReasoning.value);
    }
  }, [policePresenceRequestData]);

  const [errors, setErrors] = useState<{
    reason?: string;
    furtherReasoning?: string;
  }>({});

  const handleSubmit = () => {
    if (!selectedReason?.value) {
      setErrors({
        reason: t(
          "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.errors.reasonError",
        ),
      });
      return;
    }

    const trimmedFurtherReasoning = furtherReasoning.trim();
    if (!trimmedFurtherReasoning) {
      setErrors({
        furtherReasoning: t(
          "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.errors.otherReasonError",
        ),
      });
      return;
    }

    if (trimmedFurtherReasoning.length > 5000) {
      setErrors({
        furtherReasoning: t(
          "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.errors.otherReasonTooLong",
        ),
      });
      return;
    }

    const payload = {
      reasonValue: selectedReason.value,
      reasonLabel: selectedReason.label,
      furtherReasoning: furtherReasoning,
    };

    onRequestPresence(payload);
  };

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText={
          version === 2
            ? "Inform Police Presence"
            : "Request ongoing police support?"
        }
        subtitleText={version === 2 ? "Step 2 of 3" : undefined}
        closeDialog={() => {
          onClose();
        }}
      />
      <Box
        sx={{ display: "flex", p: 2, flexDirection: "column", gap: "1.5rem" }}
      >
        <Box>
          <FormControl>
            <FormLabel id="demo-radio-buttons-group-label">
              Do you need to request ongoing police presence at your place of
              safety?
            </FormLabel>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={requestPresenceResponse}
              onChange={(e) => {
                setRequestPresenceResponse(e.target.value as "yes" | "no");
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>

        {requestPresenceResponse === "no" && (
          <Banner
            bannerType={BannerList.WARNING}
            title={`Your response will be shared back with ${officerName}. Please note - if circumstances change you must speak to the officer directly.`}
          />
        )}

        {requestPresenceResponse === "yes" && (
          <>
            <Dropdown
              label={t(
                "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.title",
              )}
              name="reason"
              values={reasonOptions}
              selectedValue={selectedReason?.value || ""}
              onChange={(value) => {
                const selectedOption = reasonOptions.find(
                  (option) => option.value === value,
                );
                setSelectedReason(
                  selectedOption as {
                    value: PolicePresenceReason;
                    label: string;
                  } | null,
                );
                setErrors((prev) => ({ ...prev, reason: undefined }));
              }}
              errorMessage={errors.reason}
              placeholder={t(
                "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.placeholder",
              )}
            />
            <Typography>
              {t(
                "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.provideFurtherExplanation",
              )}
            </Typography>
            <TextField
              name="furtherReasoning"
              useFullWidth
              rows="4"
              label={t(
                "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.dropdown.otherReasonLabel",
              )}
              value={furtherReasoning}
              multiline
              onChange={(e) => {
                setFurtherReasoning(e);
                setErrors((prev) => ({ ...prev, furtherReasoning: undefined }));
              }}
              errorMessage={errors.furtherReasoning}
              showHelperText={errors.furtherReasoning !== undefined}
            />
            <Banner
              bannerType={BannerList.WARNING}
              body={[
                t(
                  "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.warningBannerBody1",
                ),
                t(
                  "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.warningBannerBody2",
                ),
              ]}
            />
          </>
        )}

        <Box
          sx={{
            marginTop: "2em",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            label={
              version === 2 ? t("buttonLabels.goBack") : t("buttonLabels.close")
            }
            testId="close-button"
            onClick={() => {
              if (version === 2 && onGoBack) {
                onGoBack();
              } else {
                onClose();
              }
            }}
            variant="outlined"
          />
          <Button
            label={t("buttonLabels.confirm")}
            testId="confirm-button"
            type="submit"
            disabled={isDisabled || requestPresenceResponse === null}
            onClick={() => {
              if (requestPresenceResponse === "no") {
                onDontRequestPresence();
              } else {
                handleSubmit();
              }
            }}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}
